import { useMemo } from "react";

import { useConfigurationContext } from "../root/ConfigurationContext";
import type { SponsoringStage } from "../types";
import type { DashboardFilters } from "./dashboardFilters";
import { buildDashboardFilters, todayIso } from "./dashboardFilters";

export type DashboardFilterContext = {
  /** Today as `YYYY-MM-DD`, frozen for the lifetime of the dashboard */
  today: string;
  /** The funnel stages: neither active nor closed */
  boardStages: SponsoringStage[];
  filters: DashboardFilters;
};

/**
 * The filters every dashboard widget queries with. The result is memoised so
 * the objects stay referentially stable and react-query dedupes the tiles that
 * ask the same question.
 */
export const useDashboardFilters = (): DashboardFilterContext => {
  const {
    sponsoringStages,
    sponsoringActiveStatuses,
    sponsoringClosedStatuses,
  } = useConfigurationContext();

  return useMemo(() => {
    const boardStages = sponsoringStages.filter(
      (stage) =>
        !sponsoringActiveStatuses.includes(stage.value) &&
        !sponsoringClosedStatuses.includes(stage.value),
    );
    const today = todayIso();
    return {
      today,
      boardStages,
      filters: buildDashboardFilters({
        activeStatuses: sponsoringActiveStatuses,
        boardStages: boardStages.map((stage) => stage.value),
        today,
      }),
    };
  }, [sponsoringStages, sponsoringActiveStatuses, sponsoringClosedStatuses]);
};
